import { useEffect, useRef, useState } from 'react'

// Matches the out animation in globals/stepTransition.scss
export const STEP_LEAVE_MS = 300

type Direction = 'forward' | 'back'

// Drives the shared step transition: optionally holds the current step on screen,
// slides it out, then runs `then` (which swaps in the next step, sliding it in).
// Ignores new transitions while one is running, so double taps can't skip steps.
export const useStepTransition = () => {
    const [leaving, setLeaving] = useState(false)
    const [direction, setDirection] = useState<Direction>('forward')
    const busy = useRef(false)
    const timers = useRef<ReturnType<typeof setTimeout>[]>([])

    useEffect(() => () => timers.current.forEach(clearTimeout), [])

    const reducedMotion = () => window.matchMedia?.('(prefers-reduced-motion: reduce)').matches

    const later = (fn: () => void, ms: number) => {
        timers.current.push(setTimeout(fn, reducedMotion() ? 0 : ms))
    }

    const transition = (dir: Direction, then: () => void, holdMs = 0) => {
        if (busy.current) return
        busy.current = true
        later(() => {
            setDirection(dir)
            setLeaving(true)
            later(() => {
                setLeaving(false)
                busy.current = false
                then()
            }, STEP_LEAVE_MS)
        }, holdMs)
    }

    // Global class names the step-transition mixin keys off
    const stepClassName = `${direction === 'back' ? 'back' : ''} ${leaving ? 'leaving' : ''}`

    return { transition, stepClassName, isTransitioning: () => busy.current }
}
